import { useState, useEffect, useCallback, useRef } from 'react';

// Eski anahtar ve yeni anahtar - version kontrol için
const LEGACY_STORAGE_KEY = 'filizdekorasyon_favorites';  // Eski localStorage anahtarı
const FAVORITES_STORAGE_KEY = 'filizdekorasyon_favorites_v3'; // Versiyon 3 - daha güvenli

export interface FavoriteProduct {
  id: string;
  name: string;
  image: string;
  category: string;
  slug?: string;
}

/**
 * Browser güvenlik kontrolü
 */
const isBrowser = () => typeof window !== 'undefined';

/**
 * LocalStorage güvenli erişim fonksiyonları
 */
const safeStorage = {
  /**
   * Güvenli şekilde localStorage'dan veri okuma
   */
  get: <T>(key: string, defaultValue: T): T => {
    if (!isBrowser()) return defaultValue;
    
    try {
      const item = localStorage.getItem(key);
      if (!item) return defaultValue;
      
      return JSON.parse(item) as T;
    } catch (error) {
      console.error(`[SafeStorage] Okuma hatası (${key}):`, error);
      return defaultValue;
    }
  },
  
  /**
   * Güvenli şekilde localStorage'a veri yazma
   */
  set: <T>(key: string, value: T): boolean => {
    if (!isBrowser()) return false;
    
    try {
      const serializedValue = JSON.stringify(value);
      localStorage.setItem(key, serializedValue);
      return true;
    } catch (error) {
      console.error(`[SafeStorage] Yazma hatası (${key}):`, error);
      return false;
    }
  },
  
  /**
   * Güvenli şekilde localStorage'dan veri silme
   */
  remove: (key: string): boolean => {
    if (!isBrowser()) return false;
    
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error(`[SafeStorage] Silme hatası (${key}):`, error);
      return false;
    }
  },
  
  /**
   * LocalStorage'da anahtarın olup olmadığını kontrol etme
   */
  has: (key: string): boolean => {
    if (!isBrowser()) return false;
    
    try {
      return localStorage.getItem(key) !== null;
    } catch (error) {
      console.error(`[SafeStorage] Kontrol hatası (${key}):`, error);
      return false;
    }
  }
};

/**
 * Eski verileri yeni formata geçirme (migration)
 */
/** Aynı sekmedeki tüm useFavorites örneklerini güncellemek için (storage event yalnızca diğer sekmelerde tetiklenir) */
const FAVORITES_SYNC_EVENT = 'filizdekorasyon:favorites-sync';

function notifyFavoritesSync() {
  if (isBrowser()) {
    window.dispatchEvent(new Event(FAVORITES_SYNC_EVENT));
  }
}

/** setState güncelleyicisi içinde dispatch yapılmamalı — diğer ProductCard örnekleri aynı anda setState ile patlar */
function scheduleFavoritesSync() {
  if (!isBrowser()) return;
  queueMicrotask(() => notifyFavoritesSync());
}

function readNormalizedFavoritesFromStorage(): FavoriteProduct[] {
  const storedData = safeStorage.get<FavoriteProduct[]>(FAVORITES_STORAGE_KEY, []);
  return storedData.map((item) => ({
    ...item,
    id: String(item.id),
  }));
}

const migrateLegacyFavorites = (): boolean => {
  try {
    // Zaten yeni versiyon varsa migration yapmaya gerek yok
    if (safeStorage.has(FAVORITES_STORAGE_KEY)) {
      return true;
    }
    
    // Eski veri var mı kontrol et
    if (!safeStorage.has(LEGACY_STORAGE_KEY)) {
      return true;
    }
    
    // Eski verileri oku
    const legacyData = safeStorage.get<FavoriteProduct[]>(LEGACY_STORAGE_KEY, []);
    if (legacyData.length === 0) {
      return true;
    }
    
    // Verileri normalize et
    const normalizedData = legacyData.map((item) => ({
      ...item,
      id: String(item.id)
    }));
    
    // Yeni formata yaz
    const success = safeStorage.set(FAVORITES_STORAGE_KEY, normalizedData);
    
    if (success) {
      // Başarılı migration sonrası eski verileri temizle
      safeStorage.remove(LEGACY_STORAGE_KEY);
    }
    
    return success;
  } catch (error) {
    console.error('[Favoriler] Migration sırasında hata:', error);
    return false;
  }
};

/**
 * Favoriler Hook'u - React içinde kullanım için
 */
export const useFavorites = () => {
  const [favorites, setFavorites] = useState<FavoriteProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [migrationDone, setMigrationDone] = useState(false);
  const mountedRef = useRef(false);
  
  // Eski verileri taşıma işlemi - ilk yüklemede bir kez çalışır
  useEffect(() => {
    if (!mountedRef.current && isBrowser()) {
      const migrationSuccess = migrateLegacyFavorites();
      setMigrationDone(migrationSuccess);
      mountedRef.current = true;
    }
  }, []);
  
  // Favorileri localStorage'dan yükleme
  const loadFavorites = useCallback((options?: { skipLoading?: boolean }) => {
    const skipLoading = options?.skipLoading === true;
    if (!skipLoading) {
      setIsLoading(true);
    }

    try {
      const storedData = safeStorage.get<FavoriteProduct[]>(FAVORITES_STORAGE_KEY, []);

      const normalizedData = storedData.map((item) => ({
        ...item,
        id: String(item.id),
      }));

      setFavorites(normalizedData);
    } catch (error) {
      console.error('[Favoriler] Yükleme hatası:', error);
      setFavorites([]);
    } finally {
      if (!skipLoading) {
        setIsLoading(false);
      }
    }
  }, []);
  
  // Komponent mount olduğunda favorileri yükle
  useEffect(() => {
    // Migration tamamlandıktan sonra yükle
    if (migrationDone || mountedRef.current) {
      loadFavorites();
    }
  }, [loadFavorites, migrationDone]);
  
  // Favori ekleme — her zaman localStorage ile birleştir (birden fazla ProductCard = birden fazla hook state'i; yalnız state kullanılırsa üst üste yazılır)
  const addFavorite = useCallback((product: FavoriteProduct) => {
    const normalizedProduct = {
      ...product,
      id: String(product.id),
    };

    setFavorites(() => {
      const current = readNormalizedFavoritesFromStorage();
      if (current.some((item) => String(item.id) === normalizedProduct.id)) {
        return current;
      }
      const updatedFavorites = [...current, normalizedProduct];
      safeStorage.set(FAVORITES_STORAGE_KEY, updatedFavorites);
      return updatedFavorites;
    });
    scheduleFavoritesSync();
  }, []);

  const removeFavorite = useCallback((productId: string | number) => {
    const normalizedId = String(productId);

    setFavorites(() => {
      const current = readNormalizedFavoritesFromStorage();
      const updatedFavorites = current.filter((item) => String(item.id) !== normalizedId);
      safeStorage.set(FAVORITES_STORAGE_KEY, updatedFavorites);
      return updatedFavorites;
    });
    scheduleFavoritesSync();
  }, []);
  
  // Favorilerde olup olmadığını kontrol et
  const isFavorite = useCallback((productId: string | number) => {
    const normalizedId = String(productId);
    // Favorilerde ara
    return favorites.some(item => String(item.id) === normalizedId);
  }, [favorites]);
  
  // Toggle favori - ekleme/çıkarma
  const toggleFavorite = useCallback((product: FavoriteProduct) => {
    const normalizedId = String(product.id);
    
    // Şu an favorilerde mi?
    if (isFavorite(normalizedId)) {
      // Favorilerden kaldır
      removeFavorite(normalizedId);
      return false;
    } else {
      // Favorilere ekle
      addFavorite(product);
      return true;
    }
  }, [isFavorite, removeFavorite, addFavorite]);
  
  // Tüm favorileri temizle
  const clearAllFavorites = useCallback(() => {
    safeStorage.remove(FAVORITES_STORAGE_KEY);
    setFavorites([]);
    scheduleFavoritesSync();
  }, []);

  // Aynı sekmede başka bileşenler favorileri değiştirdiğinde tüm hook örneklerini güncelle
  useEffect(() => {
    if (!isBrowser()) return;

    const syncFromStorage = () => loadFavorites({ skipLoading: true });

    window.addEventListener(FAVORITES_SYNC_EVENT, syncFromStorage);
    return () => window.removeEventListener(FAVORITES_SYNC_EVENT, syncFromStorage);
  }, [loadFavorites]);

  // Storage event dinleyerek farklı sekmelerde senkronizasyon sağla
  useEffect(() => {
    if (!isBrowser()) return;
    
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === FAVORITES_STORAGE_KEY) {
        loadFavorites({ skipLoading: true });
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [loadFavorites]);
  
  // Hook dışa aktarılan değerler ve fonksiyonlar
  return {
    favorites,
    isLoading,
    addFavorite,
    removeFavorite,
    isFavorite,
    toggleFavorite,
    clearAllFavorites,
    reloadFavorites: loadFavorites
  };
};

export default useFavorites;
